import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { SearchableSelect } from '../../components/ui/SearchableSelect.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { financeApi } from '../../services/financeApi.js';
import { salesApi } from '../../services/salesApi.js';
import { formatCurrency, todayIso } from '../../utils/formatters.js';
import { computeInvoiceTotals } from '../../utils/vat.js';

// Swiss VAT rates (2024+): normal / reduced / lodging / exempt.
const TAX_RATES = ['8.1', '2.6', '3.8', '0'];

const INTERVALS = ['MONTHLY', 'QUARTERLY', 'YEARLY'];

const DEFAULT_DUE_DAYS = 30;

function emptyLine() {
  return { accountId: '', description: '', quantity: '1', unitPrice: '0', taxRate: '8.1' };
}

function initialForm(template) {
  if (!template) {
    return {
      name: '',
      customerId: '',
      interval: 'MONTHLY',
      startDate: todayIso(),
      endDate: '',
      dueDays: String(DEFAULT_DUE_DAYS),
      autoPost: false,
      notes: '',
      lines: [emptyLine()],
    };
  }
  return {
    name: template.name || '',
    customerId: template.customerId || '',
    interval: template.interval || 'MONTHLY',
    startDate: template.startDate || todayIso(),
    endDate: template.endDate || '',
    dueDays: String(template.dueDays ?? DEFAULT_DUE_DAYS),
    autoPost: Boolean(template.autoPost),
    notes: template.notes || '',
    lines: (template.lines || []).length > 0
      ? template.lines.map((l) => ({
          accountId: l.accountId || '',
          description: l.description || '',
          quantity: String(l.quantity ?? '1'),
          unitPrice: String(l.unitPrice ?? '0'),
          taxRate: String(Number(l.taxRate ?? 0)),
        }))
      : [emptyLine()],
  };
}

/**
 * Create/edit modal for a recurring invoice template. For edit mode the parent
 * fetches the full template (incl. lines) first and passes it as `template`;
 * `customerLabel` pre-fills the SearchableSelect display for that customer.
 * Mount with a `key` per template so the form state resets between targets.
 */
export function RecurringInvoiceModal({ open, onClose, template = null, customerLabel = '' }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [form, setForm] = useState(() => initialForm(template));
  const [error, setError] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(() =>
    template?.customerId
      ? { id: template.customerId, label: customerLabel || template.customerId.slice(0, 8) }
      : null,
  );

  const accounts = useQuery({
    queryKey: ['finance', 'accounts'],
    queryFn: () => financeApi.listAccounts().then((r) => r.items || []),
    enabled: open,
  });
  const revenueAccounts = useMemo(
    () => (accounts.data || []).filter((a) => a.type === 'REVENUE' && a.isActive),
    [accounts.data],
  );

  const totals = computeInvoiceTotals(form.lines);

  const save = useMutation({
    mutationFn: (body) =>
      template ? financeApi.updateRecurring(template.id, body) : financeApi.createRecurring(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['finance', 'recurring'] });
      setError(null);
      onClose();
    },
    onError: (err) => setError(err?.message || t('common.loadError')),
  });

  function patchField(key, value) {
    setForm((p) => ({ ...p, [key]: value }));
  }

  function patchLine(idx, key, value) {
    setForm((p) => ({
      ...p,
      lines: p.lines.map((line, i) => (i === idx ? { ...line, [key]: value } : line)),
    }));
  }

  function addLine() {
    setForm((p) => ({ ...p, lines: [...p.lines, emptyLine()] }));
  }

  function removeLine(idx) {
    setForm((p) => ({
      ...p,
      lines: p.lines.length > 1 ? p.lines.filter((_, i) => i !== idx) : p.lines,
    }));
  }

  function submit(e) {
    e.preventDefault();
    setError(null);
    const hasValidLine = form.lines.some(
      (l) => l.accountId && Number(l.quantity) > 0,
    );
    if (!form.name.trim() || !form.customerId || !hasValidLine) {
      setError(t('recurring.form.invalid'));
      return;
    }
    save.mutate({
      name: form.name.trim(),
      customerId: form.customerId,
      interval: form.interval,
      startDate: form.startDate,
      endDate: form.endDate || undefined,
      dueDays: Number(form.dueDays || DEFAULT_DUE_DAYS),
      autoPost: form.autoPost,
      notes: form.notes || undefined,
      lines: form.lines.map((l) => ({
        accountId: l.accountId,
        description: l.description.trim() || ' ',
        quantity: Number(l.quantity || 0),
        unitPrice: Number(l.unitPrice || 0),
        taxRate: Number(l.taxRate),
      })),
    });
  }

  async function loadCustomers(q) {
    const { items } = await salesApi.listCustomers({ q: q || undefined, limit: 20 });
    return (items || []).map((c) => ({
      id: c.id,
      label: `${c.code} - ${c.name}`,
      sublabel: c.email || undefined,
    }));
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={template ? t('recurring.modal.editTitle') : t('recurring.action.new')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="primary" onClick={submit} disabled={save.isPending}>
            {save.isPending ? `${t('common.save')}…` : template ? t('common.save') : t('common.create')}
          </Button>
        </>
      }
    >
      <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
        <div className="field-row">
          <label className="field">
            <span className="field-label">{t('recurring.col.name')}</span>
            <input
              className="input"
              value={form.name}
              onChange={(e) => patchField('name', e.target.value)}
              required
            />
          </label>
          <label className="field">
            <span className="field-label">{t('recurring.col.customer')}</span>
            <SearchableSelect
              value={form.customerId}
              selectedOption={selectedCustomer}
              onChange={(id, option) => {
                patchField('customerId', id);
                setSelectedCustomer(option);
              }}
              loadOptions={loadCustomers}
              placeholder={t('finance.invoices.modal.searchCounterparty')}
              emptyText={t('finance.invoices.modal.noCounterparty')}
              loadingText={`${t('common.search')}…`}
            />
          </label>
        </div>
        <div className="field-row">
          <label className="field">
            <span className="field-label">{t('recurring.col.interval')}</span>
            <select
              className="select"
              value={form.interval}
              onChange={(e) => patchField('interval', e.target.value)}
            >
              {INTERVALS.map((iv) => (
                <option key={iv} value={iv}>{t(`recurring.interval.${iv}`)}</option>
              ))}
            </select>
          </label>
          <label className="field">
            <span className="field-label">{t('recurring.form.startDate')}</span>
            <input
              className="input"
              type="date"
              value={form.startDate}
              onChange={(e) => patchField('startDate', e.target.value)}
              required
            />
          </label>
          <label className="field">
            <span className="field-label">{t('recurring.form.endDate')}</span>
            <input
              className="input"
              type="date"
              value={form.endDate}
              onChange={(e) => patchField('endDate', e.target.value)}
            />
          </label>
        </div>
        <div className="field-row">
          <label className="field" style={{ flex: '0 0 160px' }}>
            <span className="field-label">{t('recurring.form.dueDays')}</span>
            <input
              className="input"
              type="number"
              min="1"
              max="365"
              step="1"
              value={form.dueDays}
              onChange={(e) => patchField('dueDays', e.target.value)}
              required
            />
          </label>
          <label className="field" style={{ flexDirection: 'row', alignItems: 'center', gap: 'var(--space-2)', alignSelf: 'flex-end' }}>
            <input
              type="checkbox"
              checked={form.autoPost}
              onChange={(e) => patchField('autoPost', e.target.checked)}
            />
            <span className="field-label" style={{ margin: 0 }}>{t('recurring.form.autoPost')}</span>
          </label>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <span className="field-label">{t('finance.invoices.modal.lines')}</span>
          <Button size="sm" variant="ghost" onClick={addLine}>+ {t('finance.invoices.modal.addLine')}</Button>
        </div>
        {form.lines.map((line, idx) => (
          <div
            key={idx}
            style={{
              display: 'flex', flexDirection: 'column', gap: 'var(--space-2)',
              padding: 'var(--space-3)', border: '1px solid var(--color-hairline)',
              borderRadius: 'var(--radius-md)',
            }}
          >
            <label className="field">
              <span className="field-label">{t('finance.invoices.modal.account')}</span>
              <select
                className="select"
                value={line.accountId}
                onChange={(e) => patchLine(idx, 'accountId', e.target.value)}
                required
              >
                <option value="">{t('finance.invoices.modal.pickCounterparty')}</option>
                {revenueAccounts.map((a) => (
                  <option key={a.id} value={a.id}>{a.code} - {a.name}</option>
                ))}
              </select>
            </label>
            <label className="field">
              <span className="field-label">{t('finance.invoices.modal.lineDescription')}</span>
              <input
                className="input"
                value={line.description}
                onChange={(e) => patchLine(idx, 'description', e.target.value)}
                required
              />
            </label>
            <div className="field-row">
              <label className="field">
                <span className="field-label">{t('finance.invoices.modal.quantity')}</span>
                <input
                  className="input"
                  type="number"
                  min="0.0001"
                  step="any"
                  value={line.quantity}
                  onChange={(e) => patchLine(idx, 'quantity', e.target.value)}
                  required
                />
              </label>
              <label className="field">
                <span className="field-label">{t('finance.invoices.modal.unitPrice')}</span>
                <input
                  className="input"
                  type="number"
                  min="0"
                  step="0.01"
                  value={line.unitPrice}
                  onChange={(e) => patchLine(idx, 'unitPrice', e.target.value)}
                  required
                />
              </label>
              <label className="field" style={{ flex: '0 0 90px' }}>
                <span className="field-label">{t('vat.rateCol')}</span>
                <select
                  className="select"
                  value={line.taxRate}
                  onChange={(e) => patchLine(idx, 'taxRate', e.target.value)}
                >
                  {TAX_RATES.map((rate) => (
                    <option key={rate} value={rate}>{rate} %</option>
                  ))}
                </select>
              </label>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span className="muted text-sm">
                {t('finance.invoices.modal.lineTotal')}: <strong>{formatCurrency(totals.lineNets[idx])}</strong>
              </span>
              {form.lines.length > 1 && (
                <Button size="sm" variant="danger" onClick={() => removeLine(idx)}>
                  {t('finance.invoices.modal.removeLine')}
                </Button>
              )}
            </div>
          </div>
        ))}

        <div style={{
          display: 'flex', flexDirection: 'column', gap: 'var(--space-1)',
          borderTop: '1px solid var(--color-hairline)', paddingTop: 'var(--space-3)',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }} className="muted text-sm">
            <span>{t('vat.net')}</span>
            <span className="mono">{formatCurrency(totals.net)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }} className="muted text-sm">
            <span>{t('vat.tax')}</span>
            <span className="mono">{formatCurrency(totals.tax)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span>{t('finance.invoices.modal.invoiceTotal')}</span>
            <strong className="mono">{formatCurrency(totals.gross)}</strong>
          </div>
        </div>

        <label className="field">
          <span className="field-label">{t('finance.invoices.modal.notes')}</span>
          <textarea
            className="textarea"
            rows={2}
            value={form.notes}
            onChange={(e) => patchField('notes', e.target.value)}
          />
        </label>

        {error && <div className="alert danger">{error}</div>}
      </form>
    </Modal>
  );
}
